import React, { useEffect, useRef, useState } from "react";
import { useChartContext } from "../../../Context/ChartsContext";
import {
  getDayDiffBetweenFiscalMonths,
  getFinancialYearMonthsPassed,
  months,
} from "../../../Utilitie";
import SingleTypeLineChart from "../../../CHARTS/SingleTypeLineChart";
import DownloadAsImage from "../../../Extra/DownloadAsImage";
import { IoIosArrowDropdownCircle } from "react-icons/io";

const LDMTEverBest = ({ defCode }) => {
  const [yearDif, setYearDif] = useState(5);
  const [openStates, setOpenStates] = useState({ selectMonth: false });
  const [selectedMonthSet, setSelectedMonthSet] = useState(new Set());
  const [viewMonth, setViewMonth] = useState(months);
  const [tableData, setTableData] = useState(null);
  const [colData, setColData] = useState([]);
  const [code, setCode] = useState(defCode);
  const [activeGraph, setActiveGraph] = useState("TOTAL");

  const { bestSheetDataObj, fetchBestSheetData } = useChartContext();
  const reportRef = useRef(null);

  useEffect(() => {
    fetchBestSheetData();
  }, []);

  const handleYearChange = (e) => setYearDif(e.target.value);
  const handleColChange = (e) => setCode(e.target.value);

  useEffect(() => {
    let exclude = ["ID", "F_YEAR", "C_YEAR", "A_MONTH", "F_MONTH", "C_MONTH"];

    if (bestSheetDataObj.bestSheetData) {
      const keys = Object.entries(
        bestSheetDataObj.bestSheetData[0].months[0].data,
      )
        .filter(([key]) => !exclude.includes(key))
        .map(([key]) => key);

      setColData(keys);
    }
  }, [bestSheetDataObj]);

  const filterData = bestSheetDataObj?.bestSheetData?.filter((fyObj) => {
    const currentYear = fyObj?.F_YEAR?.substring(0, 4);
    const baseYear = bestSheetDataObj?.bestSheetData[0]?.F_YEAR?.substring(
      0,
      4,
    );
    return currentYear > (parseInt(baseYear) - yearDif).toString();
  });

  useEffect(() => {
    const sorted = filterData
      ?.slice()
      ?.sort(
        (a, b) => Number(a?.F_YEAR.slice(0, 4)) - Number(b?.F_YEAR.slice(0, 4)),
      );
    setTableData(sorted);
  }, [bestSheetDataObj, yearDif]);

  const toggleSelection = (setFunc, selectedSet, item) => {
    const newSet = new Set(selectedSet);
    newSet.has(item) ? newSet.delete(item) : newSet.add(item);
    setFunc(newSet);
  };

  const monthPassed = getFinancialYearMonthsPassed();

  useEffect(() => {
    if (monthPassed) {
      const upper = Array.from(monthPassed).map((m) =>
        m.toUpperCase().slice(0, 3),
      );
      setSelectedMonthSet(new Set(upper));
    }
  }, []);

  useEffect(() => {
    setViewMonth(Array.from(selectedMonthSet));
  }, [selectedMonthSet]);

  const toggleDropdown = (key) => {
    setOpenStates((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getTop3Values = (fObj) => {
    const values = viewMonth
      .map((month) => {
        const match = fObj.months.find(
          (m) => m.A_MONTH?.toUpperCase() === month.toUpperCase(),
        );

        return match ? Number(match.data[code]) : null;
      })
      .filter((v) => v !== null);

    return values.sort((a, b) => b - a).slice(0, 3);
  };

  const getWorst3Values = (fObj) => {
    const values = viewMonth
      .map((month) => {
        const match = fObj.months.find(
          (m) => m.A_MONTH?.toUpperCase() === month.toUpperCase(),
        );
        return match ? Number(match.data[code]) : null;
      })
      .filter((v) => v !== null);

    return values.sort((a, b) => a - b).slice(0, 3);
  };

  const top3Map = {};
  const worst3Map = {};

  tableData?.forEach((fObj) => {
    top3Map[fObj.F_YEAR] = getTop3Values(fObj);
    worst3Map[fObj.F_YEAR] = getWorst3Values(fObj);
  });

  const key = "LDG_MT";

  const storingChartData = [];

  tableData?.forEach((yearObj) => {
    const year = yearObj.F_YEAR.replace("_", "-");

    // Total of all months
    const total = yearObj.months.reduce((sum, m) => {
      return sum + Number(m.data?.[key] || 0);
    }, 0);

    const monthsCount = yearObj.months.length || 1;

    const avg = Number((total / monthsCount).toFixed(2));

    // Push TOTAL
    storingChartData.push({
      time: year,
      [key + "_TOTAL"]: Number(total.toFixed(2)),
    });

    // Push AVG
    storingChartData.push({
      time: year,
      [key + "_AVG"]: avg,
    });
  });

  return (
    <div>
      <div ref={reportRef} className="table-responsive">
        <div className="d-flex justify-content-around bg-dark text-white p-1">
          <div>
            <select onChange={handleColChange} value={code}>
              <option>select an option</option>
              {colData.map((col, i) => (
                <option key={i} value={col}>
                  {col.replaceAll("_", " ")}
                </option>
              ))}
            </select>
            <h6>{yearDif} year data</h6>
          </div>

          <div style={{ fontSize: "12px" }}>
            select last years range
            <br />
            <input
              className="d-flex align-items-center"
              onChange={handleYearChange}
              value={yearDif}
              type="range"
              min={1}
              max={bestSheetDataObj?.bestSheetData?.length}
            />
          </div>

          {/* /////////////////////////////////// */}
          <div>
            <div
              style={{
                position: "relative",
                fontSize: 12,
                borderRadius: "5px",
              }}
            >
              <div
                className=""
                onClick={() => toggleDropdown("selectMonth")}
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  cursor: "pointer",
                  minWidth: "150px",
                  maxHeight: "50px",
                  overflow: "hidden",
                  userSelect: "none",
                }}
              >
                Select months &nbsp;
                <IoIosArrowDropdownCircle
                  style={{
                    fontSize: 15,
                    transition: "transform 0.3s ease",
                    transform: openStates.selectMonth
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              </div>

              {openStates.selectMonth && (
                <div
                  className="newUiBody"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #ccc",
                    // background: "#fff",
                    zIndex: 12,
                  }}
                >
                  <hr className="m-0 p-0" />
                  <div
                    className="row suggestion mt-1"
                    style={{
                      maxHeight: "250px",
                      minHeight: "50px",
                      overflowY: "auto",
                      color: "black",
                    }}
                  >
                    {months.length > 0 ? (
                      months.map((month, index) => (
                        <div
                          key={index}
                          className="col-md fade-in"
                          style={{ fontSize: "12px" }}
                        >
                          <label
                            className=" text-start"
                            style={{ display: "block", cursor: "pointer" }}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedMonthSet.has(month)}
                              onChange={() =>
                                toggleSelection(
                                  setSelectedMonthSet,
                                  selectedMonthSet,
                                  month,
                                )
                              }
                            />
                            &nbsp;{month}
                          </label>
                        </div>
                      ))
                    ) : (
                      <div className="col-md-12 text-center">
                        No items found
                      </div>
                    )}
                  </div>
                  <hr className="m-0 p-0" />
                  <div className="d-flex justify-content-evenly mt-1">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setSelectedMonthSet(new Set(months))}
                    >
                      Select All
                    </button>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => setSelectedMonthSet(new Set())}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DownloadAsImage reportRef={reportRef} title={code} />
        </div>

        <table
          className="table  table-striped-columns table-hover m-0 p-0"
          style={{
            fontSize: "12px",
            borderCollapse: "collapse",
            width: "100%",
            minHeight: "190px",
          }}
        >
          <thead>
            <tr>
              <th>Sn</th>
              <th>Month</th>
              {tableData?.map((f, i) => (
                <th key={i}>{f.F_YEAR}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {viewMonth.map((month, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{month}</td>

                {tableData?.map((fObj, j) => {
                  const match = fObj.months.find(
                    (m) => m.A_MONTH?.toUpperCase() === month.toUpperCase(),
                  );

                  const value = match ? Number(match.data[code]) : null;

                  const isTop3 =
                    value !== null && top3Map[fObj.F_YEAR]?.includes(value);

                  const isWorst3 =
                    value !== null && worst3Map[fObj.F_YEAR]?.includes(value);

                  return (
                    <td
                      key={j}
                      style={{
                        backgroundColor: isTop3
                          ? "#58ce73"
                          : isWorst3
                            ? "#d43a4ac4"
                            : "",
                        color: isTop3 || isWorst3 ? "#fff" : "",
                        fontWeight: isTop3 || isWorst3 ? "bold" : "normal",
                      }}
                    >
                      {value ?? "-"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SingleTypeLineChart
        data={storingChartData.filter((d) => d[code + "_" + activeGraph])}
        title={code}
        singleValue={true}
      />
    </div>
  );
};

export default LDMTEverBest;
